import React, { useMemo, useState } from "react";
import { LineChart, Plus } from "lucide-react";
import DashboardCard from "../components/DashboardCard";
import DashboardCardSkeleton from "../components/DashboardCardSkeleton";
import DashboardEmptyState from "../components/DashboardEmptyState";
import DashboardPagination from "../components/DashboardPagination";
import {
  IMarketIndices,
  IPaginationMeta,
  IPayment,
  IPropertyReturn,
  YieldBasis,
} from "../types/ownerDashboard.types";
import {
  buildPropertyLabelMap,
  resolveOwnerPropertyCardLabel,
} from "../utils/buildPropertyLabelMap";
import { formatPercentage } from "../utils/formatPercentage";
import { KenloPropertyOption } from "../utils/getKenloPropertyOptions";
import { buildRegisterPropertyOptions } from "../utils/buildRegisterPropertyOptions";
import MarketIndexChart from "./MarketIndexChart";
import PropertyReturnCard from "./PropertyReturnCard";
import RegisterPropertyValueModal from "./RegisterPropertyValueModal";
import UpdatePurchaseDateModal from "./UpdatePurchaseDateModal";
import YieldBasisToggle from "./YieldBasisToggle";

interface PropertyReturnsProps {
  marketIndices: IMarketIndices;
  propertyReturns: IPropertyReturn[];
  propertyPagination: IPaginationMeta;
  registerPropertyReturns: IPropertyReturn[];
  payments: IPayment[];
  propertyFilterOptions: KenloPropertyOption[];
  yieldBasis: YieldBasis;
  smDown: boolean;
  lgDown?: boolean;
  isMarketLoading?: boolean;
  isMarketError?: boolean;
  isPropertyLoading?: boolean;
  isPropertyError?: boolean;
  onPageChange: (page: number) => Promise<void>;
  onYieldBasisChange: (basis: YieldBasis) => Promise<void>;
  onRefresh: () => Promise<void>;
  onRetryMarketIndices: () => Promise<void>;
  onRetryPropertyReturns: () => Promise<void>;
}

export default function PropertyReturns({
  marketIndices,
  propertyReturns,
  propertyPagination,
  registerPropertyReturns,
  payments,
  propertyFilterOptions,
  yieldBasis,
  smDown,
  lgDown = false,
  isMarketLoading = false,
  isMarketError = false,
  isPropertyLoading = false,
  isPropertyError = false,
  onPageChange,
  onYieldBasisChange,
  onRefresh,
  onRetryMarketIndices,
  onRetryPropertyReturns,
}: PropertyReturnsProps) {
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [editingPropertyReturn, setEditingPropertyReturn] = useState<IPropertyReturn | null>(null);
  const padding = smDown ? "0.875rem" : lgDown ? "1rem" : "1.125rem";

  const propertyLabelMap = useMemo(
    () => buildPropertyLabelMap(payments),
    [payments]
  );

  const resolvePropertyLabel = (propertyReturn: IPropertyReturn) =>
    resolveOwnerPropertyCardLabel(propertyReturn, propertyFilterOptions, propertyLabelMap);

  const registerOptions = useMemo(
    () =>
      buildRegisterPropertyOptions(
        payments,
        propertyFilterOptions,
        registerPropertyReturns
      ),
    [payments, propertyFilterOptions, registerPropertyReturns]
  );

  const hasPropertyReturns = propertyReturns.length > 0;
  const modalPropertyOptions =
    registerOptions.length > 0 ? registerOptions : propertyFilterOptions;

  if (isMarketLoading && !hasPropertyReturns) {
    return <DashboardCardSkeleton minHeight="min-h-[24rem]" lines={6} />;
  }

  const isInitialPropertyLoad = isPropertyLoading && !hasPropertyReturns;

  return (
    <>
      <DashboardCard padding={padding} className="flex h-full min-h-0 flex-col">
        <div className="flex min-h-0 w-full flex-1 flex-col gap-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex items-center gap-2">
                <LineChart className="h-5 w-5 shrink-0 text-[#6ea8ff]" />
                <h2 className="text-base font-bold text-[#f0f6fc] sm:text-lg">Rendimentos</h2>
              </div>
              <p className="mt-1 text-xs text-[#8b949e] sm:text-sm">
                Valorização estimada pelo FIPE Zap e rendimento anual do aluguel
              </p>
            </div>

            <button
              type="button"
              onClick={() => setIsRegisterModalOpen(true)}
              className="inline-flex shrink-0 items-center justify-center gap-1.5 rounded-full border border-[#6ea8ff]/35 bg-[#6ea8ff]/10 px-4 py-2 text-xs font-semibold text-[#6ea8ff] transition-colors hover:bg-[#6ea8ff]/20 sm:text-sm"
            >
              <Plus className="h-4 w-4" />
              Cadastrar valor
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {isMarketError ? (
              <span className="rounded-full border border-white/[0.12] bg-white/[0.04] px-3 py-1 text-xs font-semibold text-[#8b949e]">
                Índices indisponíveis
              </span>
            ) : (
              <>
                <span className="rounded-full border border-[#3fb950]/30 bg-[#3fb950]/10 px-3 py-1 text-xs font-semibold text-[#3fb950]">
                  IPCA 12 meses: {formatPercentage(marketIndices.ipca_12m_accumulated_pct)}
                </span>
                <span className="rounded-full border border-[#6ea8ff]/30 bg-[#6ea8ff]/10 px-3 py-1 text-xs font-semibold text-[#6ea8ff]">
                  FIPE Zap 12 meses:{" "}
                  {formatPercentage(marketIndices.fipe_zap_12m_accumulated_pct)}
                </span>
              </>
            )}
            {!isMarketError && marketIndices.reference_month && (
              <span className="text-xs text-[#6e7681]">
                Referência: {marketIndices.reference_month}
              </span>
            )}
            {isMarketError && (
              <button
                type="button"
                onClick={onRetryMarketIndices}
                className="text-xs font-semibold text-[#6ea8ff] transition-colors hover:text-[#8bbcff]"
              >
                Tentar novamente
              </button>
            )}
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <MarketIndexChart
              title="Reajuste de aluguel (IPCA)"
              indexLabel="IPCA"
              monthlyHistory={marketIndices.ipca_monthly_history ?? []}
              isUnavailable={isMarketError}
              isLoading={isMarketLoading}
            />
            <MarketIndexChart
              title="Valorização imobiliária (FIPE Zap)"
              indexLabel="FIPE Zap"
              monthlyHistory={marketIndices.fipe_zap_monthly_history ?? []}
              isUnavailable={isMarketError}
              isLoading={isMarketLoading}
            />
          </div>

          <YieldBasisToggle value={yieldBasis} onChange={onYieldBasisChange} />

          {isInitialPropertyLoad && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="h-36 animate-pulse rounded-2xl bg-white/[0.06]" />
              <div className="h-36 animate-pulse rounded-2xl bg-white/[0.06]" />
            </div>
          )}

          {!isPropertyLoading && isPropertyError && (
            <DashboardEmptyState
              title="Não foi possível carregar os rendimentos"
              description="Verifique sua conexão e tente novamente."
              action={
                <button
                  type="button"
                  onClick={onRetryPropertyReturns}
                  className="rounded-full bg-[#6ea8ff] px-4 py-2 text-xs font-bold text-[#0d1117] transition-opacity hover:opacity-90"
                >
                  Tentar novamente
                </button>
              }
            />
          )}

          {!isPropertyLoading && !isPropertyError && !hasPropertyReturns && (
            <DashboardEmptyState
              title="Nenhum imóvel cadastrado"
              description="Informe quanto você pagou pelo imóvel para acompanhar valorização pelo FIPE Zap e rendimento anual do aluguel."
              action={
                <button
                  type="button"
                  onClick={() => setIsRegisterModalOpen(true)}
                  className="rounded-full bg-[#6ea8ff] px-4 py-2 text-xs font-bold text-[#0d1117] transition-opacity hover:opacity-90"
                >
                  Cadastrar valor do imóvel
                </button>
              }
            />
          )}

          {!isPropertyLoading && !isPropertyError && hasPropertyReturns && (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                {propertyReturns.map((propertyReturn) => (
                  <PropertyReturnCard
                    key={propertyReturn.id}
                    propertyReturn={propertyReturn}
                    propertyLabel={resolvePropertyLabel(propertyReturn)}
                    yieldBasis={yieldBasis}
                    onUpdatePurchaseDate={() => setEditingPropertyReturn(propertyReturn)}
                  />
                ))}
              </div>

              <DashboardPagination
                currentPage={propertyPagination.current_page}
                totalPages={propertyPagination.last_page}
                onPageChange={onPageChange}
              />
            </>
          )}
        </div>
      </DashboardCard>

      {isRegisterModalOpen && (
        <RegisterPropertyValueModal
          options={modalPropertyOptions}
          onClose={() => setIsRegisterModalOpen(false)}
          onSuccess={onRefresh}
        />
      )}

      {editingPropertyReturn && (
        <UpdatePurchaseDateModal
          propertyReturn={editingPropertyReturn}
          propertyLabel={resolvePropertyLabel(editingPropertyReturn)}
          onClose={() => setEditingPropertyReturn(null)}
          onSuccess={onRefresh}
        />
      )}
    </>
  );
}
